import { useState, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { 
  Plus, Edit, Trash2, CreditCard, Sparkles, Tag, Wifi, Tv, Wind, Coffee, 
  Bath, Car, Utensils, Dumbbell, Waves, Bed, ShieldCheck, TreeDeciduous, Settings
} from 'lucide-react';

// Amenity category icons
const CATEGORY_ICONS = {
  bathroom: Bath,
  entertainment: Tv,
  comfort: Wind,
  services: Utensils,
  technology: Wifi,
  outdoor: TreeDeciduous,
  safety: ShieldCheck,
  other: Sparkles
};

// Amenity icon options
const AMENITY_ICONS = [
  { value: 'wifi', label: 'WiFi', icon: Wifi },
  { value: 'tv', label: 'TV', icon: Tv },
  { value: 'ac', label: 'AC', icon: Wind },
  { value: 'coffee', label: 'Coffee', icon: Coffee },
  { value: 'bath', label: 'Bath', icon: Bath },
  { value: 'car', label: 'Parking', icon: Car },
  { value: 'food', label: 'Food', icon: Utensils },
  { value: 'gym', label: 'Gym', icon: Dumbbell },
  { value: 'pool', label: 'Pool', icon: Waves },
  { value: 'bed', label: 'Bed', icon: Bed },
  { value: 'sparkles', label: 'Other', icon: Sparkles }
];

const getAmenityIcon = (iconName) => {
  const found = AMENITY_ICONS.find(i => i.value === iconName);
  return found ? found.icon : Sparkles;
};

export default function SuperAdminMasterData() {
  const [loading, setLoading] = useState(true);
  const [idTypes, setIdTypes] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [categories, setCategories] = useState([]);
  
  // ID Type dialog
  const [idTypeDialogOpen, setIdTypeDialogOpen] = useState(false);
  const [editingIdType, setEditingIdType] = useState(null);
  const [idTypeFormData, setIdTypeFormData] = useState({ name: '' });
  
  // Amenity dialog
  const [amenityDialogOpen, setAmenityDialogOpen] = useState(false);
  const [editingAmenity, setEditingAmenity] = useState(null);
  const [amenityFormData, setAmenityFormData] = useState({
    name: '',
    icon: 'sparkles',
    description: '',
    category: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [idTypesRes, amenitiesRes, categoriesRes] = await Promise.all([
        api.get('/id-types'),
        api.get('/amenities'),
        api.get('/amenity-categories').catch(() => ({ data: [] }))
      ]);
      setIdTypes(idTypesRes.data);
      setAmenities(amenitiesRes.data);
      setCategories(categoriesRes.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load master data');
    } finally {
      setLoading(false);
    }
  };

  // ID Type handlers
  const handleIdTypeSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingIdType) {
        await api.put(`/id-types/${editingIdType.id}`, { name: idTypeFormData.name });
        toast.success('ID Type updated');
      } else {
        await api.post('/id-types', { name: idTypeFormData.name });
        toast.success('ID Type created');
      }
      setIdTypeDialogOpen(false);
      setEditingIdType(null);
      setIdTypeFormData({ name: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save ID Type');
    }
  };

  const handleDeleteIdType = async (id) => {
    if (!confirm('Are you sure you want to delete this ID Type?')) return;
    try {
      await api.delete(`/id-types/${id}`);
      toast.success('ID Type deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete ID Type');
    }
  };

  const handleToggleIdType = async (idType) => {
    try {
      await api.put(`/id-types/${idType.id}`, { is_active: !idType.is_active });
      toast.success(`ID Type ${idType.is_active ? 'deactivated' : 'activated'}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update ID Type');
    }
  };

  // Amenity handlers
  const handleAmenitySubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAmenity) {
        await api.put(`/amenities/${editingAmenity.id}`, amenityFormData);
        toast.success('Amenity updated');
      } else {
        await api.post('/amenities', amenityFormData);
        toast.success('Amenity created');
      }
      setAmenityDialogOpen(false);
      setEditingAmenity(null);
      setAmenityFormData({ name: '', icon: 'sparkles', description: '', category: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save Amenity');
    }
  };

  const handleDeleteAmenity = async (id) => {
    if (!confirm('Are you sure you want to delete this Amenity?')) return;
    try {
      await api.delete(`/amenities/${id}`);
      toast.success('Amenity deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete Amenity');
    }
  };

  const handleToggleAmenity = async (amenity) => {
    try {
      await api.put(`/amenities/${amenity.id}`, { is_active: !amenity.is_active });
      toast.success(`Amenity ${amenity.is_active ? 'deactivated' : 'activated'}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update Amenity');
    }
  };

  const openEditIdType = (idType) => {
    setEditingIdType(idType);
    setIdTypeFormData({ name: idType.name });
    setIdTypeDialogOpen(true);
  };

  const openEditAmenity = (amenity) => {
    setEditingAmenity(amenity);
    setAmenityFormData({
      name: amenity.name,
      icon: amenity.icon || 'sparkles',
      description: amenity.description || '',
      category: amenity.category || ''
    });
    setAmenityDialogOpen(true);
  };

  // Group amenities by category
  const groupedAmenities = amenities.reduce((acc, amenity) => {
    const cat = amenity.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(amenity);
    return acc;
  }, {});

  if (loading) {
    return <div className="text-center py-12">Loading master data...</div>;
  }

  return (
    <div className="space-y-8" data-testid="super-admin-master-data">
      <div>
        <h1 className="text-4xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Master Data
        </h1>
        <p className="text-muted-foreground mt-2">Manage global ID types and amenities for all hotels</p>
      </div>

      <Tabs defaultValue="id-types" className="space-y-4">
        <TabsList>
          <TabsTrigger value="id-types" className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            ID Types ({idTypes.length})
          </TabsTrigger>
          <TabsTrigger value="amenities" className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            Amenities ({amenities.length})
          </TabsTrigger>
        </TabsList>

        {/* ID Types Tab */}
        <TabsContent value="id-types">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>ID Types</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">Government ID types for guest verification</p>
              </div>
              <Button onClick={() => { setEditingIdType(null); setIdTypeFormData({ name: '' }); setIdTypeDialogOpen(true); }} data-testid="add-id-type-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add ID Type
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {idTypes.map((idType) => (
                    <TableRow key={idType.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <CreditCard className="h-4 w-4 text-blue-500" />
                          {idType.name}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={idType.is_active !== false ? 'default' : 'outline'} className={idType.is_active !== false ? 'bg-green-500' : ''}>
                          {idType.is_active !== false ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEditIdType(idType)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleToggleIdType(idType)}>
                            <Switch checked={idType.is_active !== false} className="scale-75" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteIdType(idType.id)} className="text-red-500">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {idTypes.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                        No ID types created yet. Add some common ID types like Passport, Aadhar, Driver's License.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Amenities Tab */}
        <TabsContent value="amenities">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Amenities</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">Room and hotel amenities available across all properties</p>
              </div>
              <Button onClick={() => { setEditingAmenity(null); setAmenityFormData({ name: '', icon: 'sparkles', description: '', category: '' }); setAmenityDialogOpen(true); }} data-testid="add-amenity-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Amenity
              </Button>
            </CardHeader>
            <CardContent>
              {/* Show amenities grouped by category */}
              {categories.map((category) => {
                const categoryAmenities = groupedAmenities[category] || [];
                if (categoryAmenities.length === 0) return null;
                const CategoryIcon = CATEGORY_ICONS[category] || Sparkles;
                
                return (
                  <div key={category} className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2 capitalize">
                      <CategoryIcon className="h-4 w-4" />
                      {category}
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {categoryAmenities.map((amenity) => {
                        const Icon = getAmenityIcon(amenity.icon);
                        return (
                          <div key={amenity.id} className={`p-3 border rounded-lg ${amenity.is_active ? 'bg-white' : 'bg-gray-50 opacity-60'}`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Icon className="h-4 w-4 text-orange-500" />
                                <span className="font-medium text-sm">{amenity.name}</span>
                              </div>
                              <div className="flex gap-1">
                                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => openEditAmenity(amenity)}>
                                  <Edit className="h-3 w-3" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => handleDeleteAmenity(amenity.id)}>
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                            {amenity.description && (
                              <p className="text-xs text-gray-500 mt-1">{amenity.description}</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              
              {/* Uncategorized amenities */}
              {groupedAmenities[''] && groupedAmenities[''].length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <Tag className="h-4 w-4" />
                    Uncategorized
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {groupedAmenities[''].map((amenity) => {
                      const Icon = getAmenityIcon(amenity.icon);
                      return (
                        <div key={amenity.id} className={`p-3 border rounded-lg ${amenity.is_active ? 'bg-white' : 'bg-gray-50 opacity-60'}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Icon className="h-4 w-4 text-orange-500" />
                              <span className="font-medium text-sm">{amenity.name}</span>
                            </div>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => openEditAmenity(amenity)}>
                                <Edit className="h-3 w-3" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => handleDeleteAmenity(amenity.id)}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {amenities.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <Sparkles className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p>No amenities created yet.</p>
                  <p className="text-sm">Add amenities like WiFi, AC, TV, Pool, etc.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ID Type Dialog */}
      <Dialog open={idTypeDialogOpen} onOpenChange={setIdTypeDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{editingIdType ? 'Edit ID Type' : 'Add ID Type'}</DialogTitle>
            <DialogDescription>Government-issued ID types for guest verification</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleIdTypeSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>ID Type Name *</Label>
              <Input 
                value={idTypeFormData.name} 
                onChange={(e) => setIdTypeFormData({ name: e.target.value })} 
                placeholder="e.g., Passport, Aadhar, Driver's License"
                required 
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIdTypeDialogOpen(false)}>Cancel</Button>
              <Button type="submit">{editingIdType ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Amenity Dialog */}
      <Dialog open={amenityDialogOpen} onOpenChange={setAmenityDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingAmenity ? 'Edit Amenity' : 'Add Amenity'}</DialogTitle>
            <DialogDescription>Room and hotel amenities for properties</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAmenitySubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Amenity Name *</Label>
              <Input 
                value={amenityFormData.name} 
                onChange={(e) => setAmenityFormData({ ...amenityFormData, name: e.target.value })} 
                placeholder="e.g., WiFi, Swimming Pool, Room Service"
                required 
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={amenityFormData.category || 'none'} onValueChange={(v) => setAmenityFormData({ ...amenityFormData, category: v === 'none' ? '' : v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Category</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat} className="capitalize">{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Icon</Label>
                <Select value={amenityFormData.icon} onValueChange={(v) => setAmenityFormData({ ...amenityFormData, icon: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AMENITY_ICONS.map((item) => {
                      const Icon = item.icon;
                      return (
                        <SelectItem key={item.value} value={item.value}>
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4" />
                            {item.label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Input 
                value={amenityFormData.description} 
                onChange={(e) => setAmenityFormData({ ...amenityFormData, description: e.target.value })} 
                placeholder="Brief description"
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAmenityDialogOpen(false)}>Cancel</Button>
              <Button type="submit">{editingAmenity ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
